import * as React from "react";
import {
    JSONCensusEquipmentsActionsConfirmation,
    ManualChange,
} from "../../data/models/JSONCensusEquipmentsActionsConfirmation";
import {
    CensusEquipmentStatusDetailsEnum,
    CensusEquipmentStatusesEnum,
    CensusEquipmentStatusReasons,
} from "../../data/models/CensusEnums";
import {
    JSONCensusSuggestedActionsAnalysisSummary,
    MappedSummary
} from "../../data/models/JSONCensusSuggestedActionsAnalysisSummary";
import {CensusCoolerContext} from "../CensusCollerProvider";
import {useCensusSuggestedActionsAnalysisSummary} from "../../data/hooks/CecnsusCoolerControllerHooks";
import {actionsConfirmations} from "../../data/controllers/CensusCoolerController";
import {
    addOrRemoveAndMergeManualChangeUtils,
    createConfirmedActions,
    getActionByStatusAndStatusDetailAndReasonUtils,
    getActionsByStatusAndStatusDetailsUtils,
    isMixedActionSelectedUtils,
    mapAndSortSummaryByStatusAndSuggestedAction,
    mergeConfirmedActionsUtils,
} from "./utils";
import {mapSummaryGroupedByStatusAndStatusDetail} from "../../pages/CensusStatusPage/utils";
import {isSecondStepCensus} from "../../utils";

interface SuggestedActionsContextType {
    confirmedActions: JSONCensusEquipmentsActionsConfirmation[];
    mergeConfirmedActions: (
        action: JSONCensusEquipmentsActionsConfirmation,
        isMixedActionSelected?: boolean,
    ) => void;
    addOrRemoveAndMergeManualChange: (
        action: JSONCensusEquipmentsActionsConfirmation,
        manualChange: ManualChange,
    ) => void;
    confirmActions: () => Promise<void>;
    isMixedActionSelected: (
        action: JSONCensusEquipmentsActionsConfirmation,
    ) => boolean;
    getActionByStatusAndStatusDetailsAndReason: (
        status: CensusEquipmentStatusesEnum,
        statusDetail: CensusEquipmentStatusDetailsEnum,
        inventoryStatusReason: CensusEquipmentStatusReasons
    ) => JSONCensusEquipmentsActionsConfirmation;
    resetConfirmedActions: () => void;
    isLoadingActions: boolean;
    suggestedActionsAnalysisSummary: JSONCensusSuggestedActionsAnalysisSummary;
    suggestedActionsError: Error;
    mappedSummary: MappedSummary[];
    containsEditedActions: (status: CensusEquipmentStatusesEnum,
                            statusDetail: CensusEquipmentStatusDetailsEnum,
                            inventoryStatusReason: CensusEquipmentStatusReasons) => boolean
}

interface SuggestedActionsProviderProps {
    children: React.ReactNode;
    stepNumber: number;
}

export const SuggestedActionsContext =
    React.createContext<SuggestedActionsContextType>({} as any);

export const SuggestedActionsProvider: React.FC<
    SuggestedActionsProviderProps
> = ({children, stepNumber}) => {
    const {census, lastUpdatingInfo} = React.useContext(CensusCoolerContext);
    const [isLoadingActions, setIsLoadingActions] =
        React.useState<boolean>(false);
    const [confirmedActions, setConfirmedActions] = React.useState<
        JSONCensusEquipmentsActionsConfirmation[]
    >([]);
    const [defaultConfirmedActions, setDefaultConfirmedActions] = React.useState<
        JSONCensusEquipmentsActionsConfirmation[]
    >([]);
    const [shouldFetchSummary, setShouldFetchSummary] =
        React.useState<boolean>(false);
    const [suggestedActionsAnalysisSummary, setSuggestedActionsAnalysisSummary] = React.useState<JSONCensusSuggestedActionsAnalysisSummary>();
    const [mappedSummary, setMappedSummary] = React.useState<MappedSummary[]>([]);

    const {data, isLoading, error} = useCensusSuggestedActionsAnalysisSummary(
        census?.guid,
        shouldFetchSummary,
    );

    const mergeConfirmedActions = (
        action: JSONCensusEquipmentsActionsConfirmation,
        isMixedActionSelected?: boolean,
    ): JSONCensusEquipmentsActionsConfirmation[] => {
        const mergedActions = mergeConfirmedActionsUtils(
            action,
            confirmedActions,
            isMixedActionSelected,
        );
        setConfirmedActions(mergedActions);
        return mergedActions;
    };

    const getActionByStatusAndStatusDetailsAndReason = (
        status: CensusEquipmentStatusesEnum,
        statusDetail: CensusEquipmentStatusDetailsEnum,
        inventoryStatusReason: CensusEquipmentStatusReasons
    ) => {
        return getActionByStatusAndStatusDetailAndReasonUtils(
            status,
            statusDetail,
            inventoryStatusReason,
            confirmedActions,
        );
    };

    const getDefaultActionByStatusAndStatusDetailsAndReason = (
        status: CensusEquipmentStatusesEnum,
        statusDetail: CensusEquipmentStatusDetailsEnum,
        inventoryStatusReason: CensusEquipmentStatusReasons
    ) => {
        return getActionByStatusAndStatusDetailAndReasonUtils(
            status,
            statusDetail,
            inventoryStatusReason,
            defaultConfirmedActions,
        );
    };

    const getActionsByStatusAndStatusDetails = (
        status: CensusEquipmentStatusesEnum,
        statusDetail: CensusEquipmentStatusDetailsEnum,
        actionsToCheck: JSONCensusEquipmentsActionsConfirmation[]
    ) => {
        return getActionsByStatusAndStatusDetailsUtils(
            status,
            statusDetail,
            actionsToCheck,
        );
    };

    const addOrRemoveAndMergeManualChange = (
        action: JSONCensusEquipmentsActionsConfirmation,
        newManualChange: ManualChange,
    ) => {
        setConfirmedActions(
            addOrRemoveAndMergeManualChangeUtils(
                action,
                newManualChange,
                confirmedActions,
            ),
        );
    };

    const isMixedActionSelected = (
        action: JSONCensusEquipmentsActionsConfirmation,
    ) => {
        return isMixedActionSelectedUtils(action, confirmedActions);
    };

    const confirmActions = () => {
        return actionsConfirmations(census?.guid,
            confirmedActions.map(action => {
                if (action.statusReason === CensusEquipmentStatusReasons.OTHER) {
                    return {
                        ...action,
                        statusReason: undefined
                    }
                }
                return action
            }));
    };

    const resetConfirmedActions = () => {
        setConfirmedActions(defaultConfirmedActions);
    };

    const areActionsEquals = (action: JSONCensusEquipmentsActionsConfirmation,
                              actionToCompare: JSONCensusEquipmentsActionsConfirmation) => {
        if (action === undefined && action === actionToCompare) {
            return true;
        }
        return action?.status === actionToCompare?.status &&
            action?.statusDetail === actionToCompare?.statusDetail &&
            action?.statusReason === actionToCompare?.statusReason &&
            action?.action === actionToCompare?.action &&
            action?.manualChanges.every(manualChange => actionToCompare?.manualChanges.find(
                manualChangeToCompare => manualChangeToCompare?.entityGuid === manualChange?.entityGuid && action?.action === manualChange?.action));
    }

    const containsEditedActions = (status: CensusEquipmentStatusesEnum,
                                   statusDetail: CensusEquipmentStatusDetailsEnum,
                                   inventoryStatusReason: CensusEquipmentStatusReasons) => {
        //check partial actions (status, statusDetail)
        if (inventoryStatusReason === undefined) {
            const actionsList = getActionsByStatusAndStatusDetails(status, statusDetail, confirmedActions)
            const defaultActionsList = getActionsByStatusAndStatusDetails(status, statusDetail, defaultConfirmedActions)
            if (actionsList.length !== defaultActionsList.length) {
                return true;
            }
            return defaultActionsList.some(action => {
                return actionsList.find(actionToCompare => areActionsEquals(action, actionToCompare)) === undefined
            })
        }
        const action = getActionByStatusAndStatusDetailsAndReason(status, statusDetail, inventoryStatusReason);
        const defaultAction = getDefaultActionByStatusAndStatusDetailsAndReason(status, statusDetail,
            inventoryStatusReason);
        return !areActionsEquals(defaultAction, action);
    }

    React.useEffect(() => {
        if (lastUpdatingInfo) {
            setShouldFetchSummary(
                lastUpdatingInfo &&
                lastUpdatingInfo?.processed === lastUpdatingInfo?.total &&
                lastUpdatingInfo?.total > 0,
            );
        } else if (isSecondStepCensus(census)) {
            setShouldFetchSummary(true);
        }
    }, [lastUpdatingInfo, lastUpdatingInfo?.processed, census]);

    React.useEffect(() => {
        if (!isLoading && data) {
            const sortedSuggestions = mapAndSortSummaryByStatusAndSuggestedAction(data);
            setSuggestedActionsAnalysisSummary(sortedSuggestions);
            const summary = mapSummaryGroupedByStatusAndStatusDetail(sortedSuggestions)
            setMappedSummary(summary);
            const defaultConfActions = createConfirmedActions(summary);
            setDefaultConfirmedActions([...defaultConfActions]);
            setConfirmedActions([...defaultConfActions])
        }
        setIsLoadingActions(isLoading);
    }, [data, isLoading]);

    return (
        <SuggestedActionsContext.Provider
            value={{
                confirmedActions,
                mergeConfirmedActions,
                addOrRemoveAndMergeManualChange,
                confirmActions,
                isMixedActionSelected,
                getActionByStatusAndStatusDetailsAndReason,
                resetConfirmedActions,
                isLoadingActions,
                suggestedActionsAnalysisSummary,
                suggestedActionsError: error,
                mappedSummary,
                containsEditedActions
            }}
        >
            {children}
        </SuggestedActionsContext.Provider>
    );
};
